import type { App, InjectionKey } from "vue";
import { inject, provide } from "vue";

import type { PostHog, Properties } from "posthog-js";
import { MODE } from "@/consts";
import { invokeAsync } from "./async";

import { localStorage } from "../../modules/shared-utils/storage";

const postHogKey = Symbol("postHogCapture") as InjectionKey<PostHog["capture"]>;

export type PostHogPropertiesProvider = () => Properties;
const propertiesProviderKey = Symbol(
  "postHogPropertiesProvider"
) as InjectionKey<PostHogPropertiesProvider>;

export type EventCapturer = {
  capture: PostHog["capture"];
};

// TODO: temporarily disabled by READER-552
const DISABLE_POSTHOG = true;

async function initializePostHog(): Promise<PostHog | null> {
  if (DISABLE_POSTHOG) {
    return null;
  }

  // PostHog just crashes when localStorage is not available (e.g. in private mode on Safari)
  if (!localStorage.available) {
    return null;
  }

  const { default: posthog } = await import("posthog-js");

  if (MODE === "staging") {
    posthog.init("phc_gGLSWV3zR607SlISReLIIQy3xWwkAo0uZowtRznVH8Z", {
      api_host: "https://posthog.staging-northamerica.demarque.dev",
      persistence: "localStorage+cookie",
      secure_cookie: true,
      verbose: true,
      autocapture: false,
    });
  } else if (MODE === "production") {
    posthog.init("phc_GnJeeJf4lqoeOA9m7hGRttkwr9abxbRk7vWxlSCKAzf", {
      api_host: "https://posthog.prod-europe.demarque.dev",
      persistence: "localStorage+cookie",
      secure_cookie: true,
      autocapture: false,
      debug: false,
    });
  }

  // Enable this line if you want to see posthog events in console
  posthog.debug();

  return posthog;
}

export function providePostHog(app: App) {
  const postHog = initializePostHog();

  const capture: PostHog["capture"] = (event, properties, options) => {
    invokeAsync(async () =>
      (await postHog)?.capture(event, properties, options)
    );
  };

  app.provide(postHogKey, capture);
}

export function usePostHog(
  propertiesProvider?: PostHogPropertiesProvider
): EventCapturer {
  const capture = inject(postHogKey);
  if (!capture) {
    throw new Error("usePostHog() require postHog to be provided");
  }

  const parentPropertiesProvider = inject(
    propertiesProviderKey,
    null
  ) as PostHogPropertiesProvider | null;

  let currentPropertiesProvider: PostHogPropertiesProvider | null = null;
  if (parentPropertiesProvider && propertiesProvider) {
    currentPropertiesProvider = mergePropertiesProvider(
      parentPropertiesProvider,
      propertiesProvider
    );

    provide(propertiesProviderKey, currentPropertiesProvider);
  } else if (propertiesProvider) {
    currentPropertiesProvider = propertiesProvider;
    provide(propertiesProviderKey, currentPropertiesProvider);
  }

  return capturerFor(
    capture,
    currentPropertiesProvider || parentPropertiesProvider
  );
}

function capturerFor(
  capture: PostHog["capture"],
  propertiesProvider: PostHogPropertiesProvider | null
): EventCapturer {
  return {
    capture: (event, properties, options) => {
      let mergedProperties: Properties | null = null;
      if (propertiesProvider) {
        mergedProperties = propertiesProvider();
        if (properties) {
          mergedProperties = { ...mergedProperties, ...properties };
        }
      } else if (properties) {
        mergedProperties = properties;
      }
      capture(event, mergedProperties, options);
    },
  };
}

function mergePropertiesProvider(
  parent: PostHogPropertiesProvider,
  children: PostHogPropertiesProvider
): PostHogPropertiesProvider {
  return () => ({ ...parent(), ...children() });
}
